import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Book, Upload, Trash2, Download, RefreshCw, FileSpreadsheet, Plus, Database, CalendarClock, Sparkles, Check, X, Pencil } from "lucide-react";

const CATS = [
  { v: "rsaw", label: "RSAW" },
  { v: "technical_justification", label: "Technical Justification" },
  { v: "evidence_request_tool", label: "Evidence Request Tool" },
  { v: "implementation_plan", label: "Implementation Plan" },
  { v: "other", label: "Other" },
];
const catLabel = (v) => (CATS.find((c) => c.v === v)?.label || v);

export default function ConsoleNerc() {
  return (
    <div className="space-y-8" data-testid="console-nerc-panel">
      <StandardsCatalog />
      <OneStopShop />
      <ImplementationPlans />
      <DocumentLibrary />
    </div>
  );
}

/* ---------- Standards Catalog ---------- */
function StandardsCatalog() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ standard_id: "", title: "", purpose: "", status: "currently_enforceable", effective_date: "" });
  const [busy, setBusy] = useState(false);
  const [inclInactive, setInclInactive] = useState(false);

  const load = useCallback(() => {
    api.get("/standards").then(({ data }) => setRows(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.standard_id.trim()) { toast.error("Standard ID required"); return; }
    setBusy(true);
    try {
      await api.post("/standards", { ...form, effective_date: form.effective_date || null });
      toast.success("Standard added");
      setForm({ standard_id: "", title: "", purpose: "", status: "currently_enforceable", effective_date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete standard ${s.standard_id} and its requirements?`)) return;
    try { await api.delete(`/standards/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const importOss = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("include_inactive", inclInactive ? "true" : "false");
      const { data } = await api.post("/console/standards/import-oss", fd);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed (import the One Stop Shop first)"); }
    finally { setBusy(false); }
  };

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-bold flex items-center gap-2"><Database size={18} className="text-primary" /> Standards Catalog</h3>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={inclInactive} onChange={(e) => setInclInactive(e.target.checked)} data-testid="oss-incl-inactive" /> include inactive
          </label>
          <Button variant="outline" className="rounded-none" onClick={importOss} disabled={busy} data-testid="import-standards-oss-btn">
            <Database size={15} className="mr-1.5" /> Import from One Stop Shop
          </Button>
        </div>
      </div>
      <div className="grid md:grid-cols-6 gap-2 items-end border border-border bg-muted/30 p-3">
        <div className="md:col-span-1"><Label className="text-xs">Standard ID</Label><Input value={form.standard_id} onChange={(e) => setForm({ ...form, standard_id: e.target.value.toUpperCase() })} className="rounded-none" data-testid="std-field-id" /></div>
        <div className="md:col-span-2"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" data-testid="std-field-title" /></div>
        <div className="md:col-span-1"><Label className="text-xs">Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="rounded-none" data-testid="std-field-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="currently_enforceable">Enforceable</SelectItem>
              <SelectItem value="future_enforceable">Future</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1"><Label className="text-xs">Effective</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="rounded-none" data-testid="std-field-eff" /></div>
        <div className="md:col-span-1"><Button className="rounded-none w-full" onClick={add} disabled={busy} data-testid="std-add-btn"><Plus size={15} className="mr-1" /> Add</Button></div>
      </div>
      <div className="border border-border max-h-72 overflow-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0"><tr className="text-left text-xs uppercase text-muted-foreground"><th className="p-2">Standard</th><th className="p-2">Title</th><th className="p-2">Status</th><th className="p-2"></th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="p-2 font-mono text-xs">{s.standard_id}</td>
                <td className="p-2">{s.title}</td>
                <td className="p-2 text-xs">{(s.status || "").replace("_", " ")}</td>
                <td className="p-2 text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => del(s)} data-testid={`std-del-${s.id}`}><Trash2 size={14} /></Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground text-xs">No standards yet — add one or import from the One Stop Shop.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ---------- One Stop Shop ---------- */
function OneStopShop() {
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(() => { api.get("/console/one-stop-shop/status").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const importUrl = async () => {
    setBusy("url");
    try { const fd = new FormData(); fd.append("from_url", "true"); const { data } = await api.post("/console/one-stop-shop/import", fd); toast.success(`Imported ${data.count} standards from NERC`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Download failed"); }
    finally { setBusy(""); }
  };
  const importFile = async (f) => {
    if (!f) return; setBusy("file");
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/console/one-stop-shop/import", fd); toast.success(`Imported ${data.count} standards`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const check = async () => {
    setBusy("check");
    try { const { data } = await api.post("/console/one-stop-shop/check-update"); toast[data.update_available ? "warning" : "success"](data.update_available ? "A newer One Stop Shop is available from NERC" : "You have the latest version"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Check failed"); }
    finally { setBusy(""); }
  };

  return (
    <section className="space-y-3">
      <h3 className="text-lg font-bold flex items-center gap-2"><FileSpreadsheet size={18} className="text-primary" /> NERC One Stop Shop</h3>
      {meta.update_available && (
        <div className="border border-amber-300 bg-amber-50 text-amber-900 text-sm px-3 py-2" data-testid="oss-update-banner">
          A newer One Stop Shop is available from NERC. Click <b>Import latest from NERC</b> to refresh.
        </div>
      )}
      <div className="border border-border bg-muted/30 p-3 flex flex-wrap items-center gap-2">
        <Button className="rounded-none" onClick={importUrl} disabled={!!busy} data-testid="oss-import-url-btn"><Download size={15} className="mr-1.5" /> {busy === "url" ? "Importing…" : "Import latest from NERC"}</Button>
        <Button variant="outline" className="rounded-none" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="oss-import-file-btn"><Upload size={15} className="mr-1.5" /> Upload .xlsx</Button>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} />
        <Button variant="ghost" className="rounded-none" onClick={check} disabled={!!busy} data-testid="oss-check-btn"><RefreshCw size={15} className="mr-1.5" /> {busy === "check" ? "Checking…" : "Check for updates"}</Button>
        <div className="ml-auto text-xs text-muted-foreground">
          {meta.count ? `${meta.count} standards • imported ${(meta.imported_at || "").slice(0, 10) || "—"}` : "Not imported yet"}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Imported standards are searchable by every entity on their <b>NERC Library → One Stop Shop</b> page.</p>
    </section>
  );
}

/* ---------- Implementation Plans ---------- */
function ImplementationPlans() {
  const [plans, setPlans] = useState([]);
  const [sid, setSid] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [openSid, setOpenSid] = useState("");
  const [draft, setDraft] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/console/implementation-plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = (p) => { setOpenSid(p.standard_id); setDraft(structuredClone(p)); };

  const analyze = async (standardId, f) => {
    const id = (standardId || "").trim().toUpperCase();
    if (!id) { toast.error("Enter a Standard ID (e.g. CIP-003-9)"); return; }
    setBusy("analyze:" + id);
    try {
      const fd = new FormData();
      if (f) fd.append("file", f);
      const { data } = await api.post(`/console/implementation-plans/${encodeURIComponent(id)}/analyze`, fd);
      toast.success(`Analyzed ${id}: ${data.requirement_dates?.length || 0} requirement date(s) — review & publish`);
      setSid(""); setFile(null); if (fileRef.current) fileRef.current.value = "";
      load(); openEditor(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Analysis failed"); }
    finally { setBusy(""); }
  };

  const setRow = (i, k, v) => setDraft((d) => { const rd = [...d.requirement_dates]; rd[i] = { ...rd[i], [k]: v }; return { ...d, requirement_dates: rd }; });
  const addRow = () => setDraft((d) => ({ ...d, requirement_dates: [...d.requirement_dates, { req_number: "", effective_date: "", note: "" }] }));
  const delRow = (i) => setDraft((d) => ({ ...d, requirement_dates: d.requirement_dates.filter((_, x) => x !== i) }));

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/console/implementation-plans/${encodeURIComponent(draft.standard_id)}`, {
        standard_effective_date: draft.standard_effective_date || null,
        ferc_order: draft.ferc_order || "",
        requirement_dates: draft.requirement_dates,
        summary: draft.summary || "",
      });
      toast.success("Saved"); setDraft(structuredClone(data)); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(""); }
  };

  const publish = async (p, on) => {
    setBusy("pub:" + p.standard_id);
    try {
      await api.post(`/console/implementation-plans/${encodeURIComponent(p.standard_id)}/${on ? "publish" : "unpublish"}`);
      toast.success(on ? "Published — dates now used in all entity manuals" : "Unpublished (back to draft)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete the implementation plan analysis for ${p.standard_id}?`)) return;
    try { await api.delete(`/console/implementation-plans/${encodeURIComponent(p.standard_id)}`); toast.success("Deleted"); if (openSid === p.standard_id) { setOpenSid(""); setDraft(null); } load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <section className="space-y-3" data-testid="console-implplans">
      <h3 className="text-lg font-bold flex items-center gap-2"><CalendarClock size={18} className="text-primary" /> Implementation Plans <span className="text-xs font-normal text-muted-foreground">(per-requirement effective dates)</span></h3>
      <p className="text-xs text-muted-foreground">Analyze a Standard's NERC Implementation Plan to extract per-requirement effective dates with AI (Claude). Review, edit, then Publish — published dates appear in every entity's Policy Manual. If NERC has no linked plan, upload the PDF.</p>
      <div className="border border-border bg-muted/30 p-3 flex flex-wrap items-end gap-2">
        <div><Label className="text-xs">Standard ID</Label><Input value={sid} onChange={(e) => setSid(e.target.value.toUpperCase())} placeholder="CIP-003-9" className="rounded-none w-40" data-testid="implplan-sid" /></div>
        <div><Label className="text-xs">PDF (optional — falls back to OSS link)</Label><Input ref={fileRef} type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0])} className="rounded-none" data-testid="implplan-file" /></div>
        <Button className="rounded-none" onClick={() => analyze(sid, file)} disabled={!!busy} data-testid="implplan-analyze-btn"><Sparkles size={15} className="mr-1.5" /> {busy.startsWith("analyze") ? "Analyzing…" : "Analyze"}</Button>
      </div>
      <div className="space-y-2">
        {plans.map((p) => (
          <div key={p.standard_id} className="border border-border bg-white" data-testid={`implplan-row-${p.standard_id}`}>
            <div className="flex items-center gap-3 px-3 py-2 flex-wrap">
              <span className="font-mono text-sm font-semibold">{p.standard_id}</span>
              <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] font-semibold uppercase ${p.status === "published" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`} data-testid={`implplan-status-${p.standard_id}`}>{p.status}</span>
              <span className="text-xs text-muted-foreground">{p.requirement_dates?.length || 0} req date(s) · std eff {p.standard_effective_date || "—"}</span>
              <div className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => (openSid === p.standard_id ? (setOpenSid(""), setDraft(null)) : openEditor(p))} data-testid={`implplan-edit-${p.standard_id}`}><Pencil size={13} className="mr-1" /> Review</Button>
                {p.status === "published"
                  ? <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" disabled={!!busy} onClick={() => publish(p, false)} data-testid={`implplan-unpub-${p.standard_id}`}>Unpublish</Button>
                  : <Button size="sm" className="rounded-none h-7 text-xs" disabled={!!busy} onClick={() => publish(p, true)} data-testid={`implplan-pub-${p.standard_id}`}><Check size={13} className="mr-1" /> Publish</Button>}
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => del(p)} data-testid={`implplan-del-${p.standard_id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
            {openSid === p.standard_id && draft && (
              <div className="border-t border-border p-3 space-y-2 bg-muted/20" data-testid={`implplan-editor-${p.standard_id}`}>
                {draft.summary && <p className="text-xs text-foreground/80 italic">AI summary: {draft.summary}</p>}
                <div className="flex flex-wrap items-end gap-2">
                  <div><Label className="text-xs">Standard Effective Date</Label><Input type="date" value={(draft.standard_effective_date || "").slice(0, 10)} onChange={(e) => setDraft({ ...draft, standard_effective_date: e.target.value })} className="rounded-none w-44" data-testid="implplan-std-eff" /></div>
                  <div><Label className="text-xs">FERC Order</Label><Input value={draft.ferc_order || ""} onChange={(e) => setDraft({ ...draft, ferc_order: e.target.value })} className="rounded-none w-52" data-testid="implplan-ferc" /></div>
                  {draft.impl_plan_link && <a href={draft.impl_plan_link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline pb-2">Source plan ↗</a>}
                </div>
                <div className="border border-border bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-muted"><tr className="text-left text-xs uppercase text-muted-foreground"><th className="p-2 w-28">Requirement</th><th className="p-2 w-40">Effective Date</th><th className="p-2">Note</th><th className="p-2 w-10"></th></tr></thead>
                    <tbody>
                      {draft.requirement_dates.map((r, i) => (
                        <tr key={i} className="border-t border-border" data-testid={`implplan-dr-${i}`}>
                          <td className="p-1"><Input value={r.req_number} onChange={(e) => setRow(i, "req_number", e.target.value.toUpperCase())} placeholder="R1" className="rounded-none h-8" data-testid={`implplan-dr-req-${i}`} /></td>
                          <td className="p-1"><Input type="date" value={(r.effective_date || "").slice(0, 10)} onChange={(e) => setRow(i, "effective_date", e.target.value)} className="rounded-none h-8" data-testid={`implplan-dr-eff-${i}`} /></td>
                          <td className="p-1"><Input value={r.note || ""} onChange={(e) => setRow(i, "note", e.target.value)} placeholder="phasing / context" className="rounded-none h-8" data-testid={`implplan-dr-note-${i}`} /></td>
                          <td className="p-1 text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => delRow(i)} data-testid={`implplan-dr-del-${i}`}><X size={13} /></Button></td>
                        </tr>
                      ))}
                      {draft.requirement_dates.length === 0 && <tr><td colSpan={4} className="p-3 text-center text-xs text-muted-foreground">No per-requirement dates — requirements will inherit the Standard Effective Date.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" className="rounded-none" onClick={addRow} data-testid="implplan-addrow"><Plus size={14} className="mr-1" /> Add requirement</Button>
                  <Button size="sm" variant="ghost" className="rounded-none" disabled={!!busy} onClick={() => analyze(draft.standard_id, null)} data-testid="implplan-reanalyze"><Sparkles size={14} className="mr-1" /> Re-analyze</Button>
                  <Button size="sm" className="rounded-none ml-auto" disabled={busy === "save"} onClick={save} data-testid="implplan-save"><Check size={14} className="mr-1" /> {busy === "save" ? "Saving…" : "Save"}</Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {plans.length === 0 && <div className="text-center text-muted-foreground text-xs py-6 border border-dashed border-border">No implementation plans analyzed yet — enter a Standard ID above.</div>}
      </div>
    </section>
  );
}

/* ---------- Document Library ---------- */
function DocumentLibrary() {
  const [files, setFiles] = useState([]);
  const [f, setF] = useState({ category: "rsaw", title: "", description: "", standard_number: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => { api.get("/nerc-library").then(({ data }) => setFiles(data.files)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("category", f.category); fd.append("title", f.title);
      fd.append("description", f.description); fd.append("standard_number", f.standard_number);
      await api.post("/console/nerc-library", fd);
      toast.success("Uploaded — now available to all entities");
      setF({ category: "rsaw", title: "", description: "", standard_number: "" }); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };
  const del = async (x) => {
    if (!window.confirm(`Delete "${x.title}"?`)) return;
    try { await api.delete(`/console/nerc-library/${x.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <section className="space-y-3">
      <h3 className="text-lg font-bold flex items-center gap-2"><Book size={18} className="text-primary" /> Document Library <span className="text-xs font-normal text-muted-foreground">(RSAWs, Technical Justifications, Evidence Request Tool, other)</span></h3>
      <div className="grid md:grid-cols-6 gap-2 items-end border border-border bg-muted/30 p-3">
        <div className="md:col-span-1"><Label className="text-xs">Category</Label>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger className="rounded-none" data-testid="lib-field-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2"><Label className="text-xs">Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-none" data-testid="lib-field-title" /></div>
        <div className="md:col-span-1"><Label className="text-xs">Standard # (opt)</Label><Input value={f.standard_number} onChange={(e) => setF({ ...f, standard_number: e.target.value.toUpperCase() })} placeholder="CIP-007-6" className="rounded-none" data-testid="lib-field-std" /></div>
        <div className="md:col-span-2"><Label className="text-xs">File</Label><Input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0])} className="rounded-none" data-testid="lib-field-file" /></div>
        <div className="md:col-span-6"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description (optional)" rows={2} className="rounded-none" data-testid="lib-field-desc" /></div>
        <div className="md:col-span-1"><Button className="rounded-none w-full" onClick={upload} disabled={busy} data-testid="lib-upload-btn"><Upload size={15} className="mr-1" /> {busy ? "…" : "Upload"}</Button></div>
      </div>
      <div className="space-y-1.5">
        {files.map((x) => (
          <div key={x.id} className="flex items-center gap-3 border border-border bg-white px-3 py-2" data-testid={`lib-row-${x.id}`}>
            <span className="px-1.5 py-0.5 border rounded-sm text-[10px] font-semibold bg-slate-100 text-slate-700 uppercase">{catLabel(x.category)}</span>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{x.title}</div>
              <div className="text-xs text-muted-foreground truncate">{x.standard_number ? `${x.standard_number} · ` : ""}{x.filename}{x.description ? ` — ${x.description}` : ""}</div>
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8" title="Download" onClick={() => downloadBlob(`/nerc-library/${x.id}/download`, x.filename)} data-testid={`lib-dl-${x.id}`}><Download size={15} /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Delete" onClick={() => del(x)} data-testid={`lib-del-${x.id}`}><Trash2 size={15} /></Button>
          </div>
        ))}
        {files.length === 0 && <div className="text-center text-muted-foreground text-xs py-6 border border-dashed border-border">No files yet.</div>}
      </div>
    </section>
  );
}
